import * as Api from '@ViewModels';
import { FilterOperator, InsurancePolicyFilterProperty } from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useDebounceValue } from '../../../../../../hooks/useDebounceValue';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { SimpleAutoCompleteSearchField } from '../../../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { styleSheet as simpleAutoCompleteStyleSheet } from '../../../../../components/autocomplete/SimpleAutoCompleteSearchField/styles';
import { MoneyFilterDropdown } from '../../../../../components/dataBoards/policies/MoneyFilterDropdown';
import { PolicyCarrierSearch } from '../../../../../components/dataBoards/policies/PolicyCarrierSearch';
import { PolicyLineOfBusinessFilterDropdown } from '../../../../../components/dataBoards/policies/PolicyLineOfBusinessFilterDropdown';
import { FutureRenewalDateFilterDropdown } from '../../../../../components/dataBoards/policies/FutureRenewalDateFilterDropdown';
import { baseStyleSheet } from '../../../../../styles/styles';
import { styleSheet as dbStyleSheet } from '../../../../DataBoards/styles';
import { styleSheet } from './styles';

interface PoliciesFiltersProps {
	filterCriteria: Api.IInsurancePolicyFilterCriteria[];
	onFilterChange: (criteria: Api.IInsurancePolicyFilterCriteria[]) => void;
}

export const PoliciesFilters: React.FC<PoliciesFiltersProps> = ({ filterCriteria, onFilterChange }) => {
	const userSession = useUserSession();
	const insuranceIntegrationEnabled = userSession.hasInsuranceIntegrationConfigured;
	const canEdit = !insuranceIntegrationEnabled;

	// Policy Number - always active
	const [policyNumberSearch, setPolicyNumberSearch] = React.useState('');
	const debouncedPolicyNumberSearch = useDebounceValue(policyNumberSearch, 500);

	// Policy Holder - always visible
	const [selectedContact, setSelectedContact] = React.useState<Api.IContact>(null);

	// Carrier - always visible
	const [selectedCarrierId, setSelectedCarrierId] = React.useState<string>(null);

	// Policy Owner - always visible
	const [selectedOwner, setSelectedOwner] = React.useState<Api.IContact>(null);

	// Line of Business - always visible
	const [selectedPolicyLineOfBusinessIds, setSelectedPolicyLineOfBusinessIds] = React.useState<string[]>([]);

	// Premium filter
	const [minimumPremium, setMinimumPremium] = React.useState<string>('');
	const [maximumPremium, setMaximumPremium] = React.useState<string>('');

	// Renewal Date filter
	const [selectedMinimumRenewalDate, setSelectedMinimumRenewalDate] = React.useState<string>(null);
	const [selectedMaximumRenewalDate, setSelectedMaximumRenewalDate] = React.useState<string>(null);

	// Commissions filter (conditional)
	const [minimumCommissions, setMinimumCommissions] = React.useState<string>('');
	const [maximumCommissions, setMaximumCommissions] = React.useState<string>('');

	// Servicing Role filters (conditional)
	const [selectedServicingRole1User, setSelectedServicingRole1User] = React.useState<Api.IContact>(null);
	const [selectedServicingRole2User, setSelectedServicingRole2User] = React.useState<Api.IContact>(null);

	// Ref to prevent infinite loops when parsing from URL
	const isParsingFromUrlRef = React.useRef(false);
	// Initialize with empty string to ensure parsing runs on mount
	const previousCriteriaStringRef = React.useRef<string>('');
	// Ref to track clear operations for search remounting
	const searchClearCounterRef = React.useRef(0);
	// Ref to track if this is the initial mount
	const isInitialMountRef = React.useRef(true);

	// Convert filter state to criteria
	const criteria = React.useMemo(() => {
		const result: Api.IInsurancePolicyFilterCriteria[] = [];

		// Policy Number filter
		if (debouncedPolicyNumberSearch) {
			result.push({
				property: InsurancePolicyFilterProperty.PolicyNumber,
				value: debouncedPolicyNumberSearch,
			});
		}

		// Policy Holder filter
		if (selectedContact?.id) {
			result.push({
				property: InsurancePolicyFilterProperty.Contact,
				value: selectedContact.id,
			});
		}

		// Carrier filter
		if (selectedCarrierId) {
			result.push({
				property: InsurancePolicyFilterProperty.Carrier,
				value: selectedCarrierId,
			});
		}

		// Policy Owner filter
		if (selectedOwner?.id) {
			result.push({
				property: InsurancePolicyFilterProperty.OwnerId,
				value: selectedOwner.id,
			});
		}

		// Line of Business filter (OR operator with nested criteria)
		if (selectedPolicyLineOfBusinessIds.length > 0) {
			result.push({
				criteria: selectedPolicyLineOfBusinessIds.map(policyLineOfBusinessId => {
					return {
						property: InsurancePolicyFilterProperty.LineOfBusiness,
						value: policyLineOfBusinessId,
					};
				}),
				op: FilterOperator.Or,
			});
		}

		// Premium filter
		if (minimumPremium || maximumPremium) {
			if (minimumPremium) {
				result.push({
					property: InsurancePolicyFilterProperty.Premium,
					op: FilterOperator.Gt,
					value: minimumPremium,
				});
			}
			if (maximumPremium) {
				result.push({
					property: InsurancePolicyFilterProperty.Premium,
					op: FilterOperator.Lt,
					value: maximumPremium,
				});
			}
		}

		// Renewal Date filter
		if (selectedMinimumRenewalDate || selectedMaximumRenewalDate) {
			if (selectedMinimumRenewalDate) {
				result.push({
					property: InsurancePolicyFilterProperty.RenewalDate,
					op: FilterOperator.Gt,
					value: moment(selectedMinimumRenewalDate).format('YYYY-MM-DD'),
				});
			}
			if (selectedMaximumRenewalDate) {
				result.push({
					property: InsurancePolicyFilterProperty.RenewalDate,
					op: FilterOperator.Lt,
					value: moment(selectedMaximumRenewalDate).format('YYYY-MM-DD'),
				});
			}
		}

		// Commissions filter (conditional)
		if (minimumCommissions || maximumCommissions) {
			if (minimumCommissions) {
				result.push({
					property: InsurancePolicyFilterProperty.Commissions,
					op: FilterOperator.Gt,
					value: minimumCommissions,
				});
			}
			if (maximumCommissions) {
				result.push({
					property: InsurancePolicyFilterProperty.Commissions,
					op: FilterOperator.Lt,
					value: maximumCommissions,
				});
			}
		}

		// Servicing Role 1 User filter (conditional)
		if (selectedServicingRole1User?.id) {
			result.push({
				property: InsurancePolicyFilterProperty.ServicingRole1UserId,
				value: selectedServicingRole1User.id,
			});
		}

		// Servicing Role 2 User filter (conditional)
		if (selectedServicingRole2User?.id) {
			result.push({
				property: InsurancePolicyFilterProperty.ServicingRole2UserId,
				value: selectedServicingRole2User.id,
			});
		}

		return result;
	}, [
		debouncedPolicyNumberSearch,
		selectedContact,
		selectedCarrierId,
		selectedOwner,
		selectedPolicyLineOfBusinessIds,
		minimumPremium,
		maximumPremium,
		selectedMinimumRenewalDate,
		selectedMaximumRenewalDate,
		minimumCommissions,
		maximumCommissions,
		selectedServicingRole1User,
		selectedServicingRole2User,
	]);

	// Notify parent when criteria changes (but not when parsing from URL)
	React.useEffect(() => {
		if (isParsingFromUrlRef.current) {
			return;
		}
		// Skip on initial mount if we have URL criteria to parse (to avoid clearing URL filters)
		if (isInitialMountRef.current && filterCriteria && filterCriteria.length > 0) {
			return;
		}
		const criteriaString = JSON.stringify(criteria);
		if (criteriaString !== previousCriteriaStringRef.current) {
			previousCriteriaStringRef.current = criteriaString;
			onFilterChange(criteria);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [criteria]);

	// Parse URL criteria and initialize filter state
	React.useEffect(() => {
		const criteriaString = JSON.stringify(filterCriteria || []);
		const isInitialMount = isInitialMountRef.current;

		// On initial mount, always parse. Otherwise, only parse if criteria changed.
		if (!isInitialMount && criteriaString === previousCriteriaStringRef.current) {
			return; // No change, skip parsing
		}

		previousCriteriaStringRef.current = criteriaString;
		isParsingFromUrlRef.current = true;

		// Mark that initial mount is complete after first parse
		if (isInitialMount) {
			isInitialMountRef.current = false;
		}
		if (!filterCriteria || filterCriteria.length === 0) {
			// Reset all filters if no criteria
			searchClearCounterRef.current += 1;
			setPolicyNumberSearch('');
			setSelectedContact(null);
			setSelectedCarrierId(null);
			setSelectedOwner(null);
			setSelectedPolicyLineOfBusinessIds([]);
			setMinimumPremium('');
			setMaximumPremium('');
			setSelectedMinimumRenewalDate(null);
			setSelectedMaximumRenewalDate(null);
			setMinimumCommissions('');
			setMaximumCommissions('');
			setSelectedServicingRole1User(null);
			setSelectedServicingRole2User(null);
			// Reset the flag after state updates
			setTimeout(() => {
				isParsingFromUrlRef.current = false;
			}, 0);
			return;
		}

		// Parse criteria to initialize filter state
		let parsedPolicyNumber = '';
		let parsedContact: Api.IContact = null;
		let parsedCarrierId: string = null;
		let parsedOwner: Api.IContact = null;
		let parsedLineOfBusinessIds: string[] = [];
		let parsedMinPremium = '';
		let parsedMaxPremium = '';
		let parsedMinRenewalDate: string = null;
		let parsedMaxRenewalDate: string = null;
		let parsedMinCommissions = '';
		let parsedMaxCommissions = '';
		let parsedServicingRole1User: Api.IContact = null;
		let parsedServicingRole2User: Api.IContact = null;

		// Helper to recursively parse nested criteria
		const parseCriteriaRecursive = (criteriaList: Api.IInsurancePolicyFilterCriteria[]) => {
			for (const crit of criteriaList) {
				if (crit.criteria && crit.criteria.length > 0) {
					// Handle OR operator for LineOfBusiness
					if (crit.op === FilterOperator.Or) {
						parsedLineOfBusinessIds = crit.criteria
							.filter(c => c.property === InsurancePolicyFilterProperty.LineOfBusiness)
							.map(c => c.value);
					} else {
						parseCriteriaRecursive(crit.criteria);
					}
				} else if (crit.property) {
					const property = crit.property as string;
					switch (property) {
						case InsurancePolicyFilterProperty.PolicyNumber:
						case 'PolicyNumber':
							if (crit.value) {
								parsedPolicyNumber = crit.value;
							}
							break;
						case InsurancePolicyFilterProperty.Contact:
						case 'Contact':
							if (crit.value) {
								parsedContact = { id: crit.value } as Api.IContact;
							}
							break;
						case InsurancePolicyFilterProperty.Carrier:
						case 'Carrier':
							if (crit.value) {
								parsedCarrierId = crit.value;
							}
							break;
						case InsurancePolicyFilterProperty.OwnerId:
						case 'OwnerId':
							if (crit.value) {
								parsedOwner = { id: crit.value } as Api.IContact;
							}
							break;
						case InsurancePolicyFilterProperty.LineOfBusiness:
						case 'LineOfBusiness':
							if (crit.value) {
								parsedLineOfBusinessIds.push(crit.value);
							}
							break;
						case InsurancePolicyFilterProperty.Premium:
						case 'Premium':
							if (crit.op === FilterOperator.Gt && crit.value) {
								parsedMinPremium = crit.value;
							} else if (crit.op === FilterOperator.Lt && crit.value) {
								parsedMaxPremium = crit.value;
							}
							break;
						case InsurancePolicyFilterProperty.RenewalDate:
						case 'RenewalDate':
							if (crit.op === FilterOperator.Gt && crit.value) {
								parsedMinRenewalDate = crit.value;
							} else if (crit.op === FilterOperator.Lt && crit.value) {
								parsedMaxRenewalDate = crit.value;
							}
							break;
						case InsurancePolicyFilterProperty.Commissions:
						case 'Commissions':
							if (crit.op === FilterOperator.Gt && crit.value) {
								parsedMinCommissions = crit.value;
							} else if (crit.op === FilterOperator.Lt && crit.value) {
								parsedMaxCommissions = crit.value;
							}
							break;
						case InsurancePolicyFilterProperty.ServicingRole1UserId:
						case 'ServicingRole1UserId':
							if (crit.value) {
								parsedServicingRole1User = { id: crit.value } as Api.IContact;
							}
							break;
						case InsurancePolicyFilterProperty.ServicingRole2UserId:
						case 'ServicingRole2UserId':
							if (crit.value) {
								parsedServicingRole2User = { id: crit.value } as Api.IContact;
							}
							break;
						default:
							break;
					}
				}
			}
		};

		parseCriteriaRecursive(filterCriteria);

		// Update state only if values changed to avoid infinite loops
		if (parsedPolicyNumber !== policyNumberSearch) {
			setPolicyNumberSearch(parsedPolicyNumber);
		}
		if (parsedContact?.id !== selectedContact?.id) {
			setSelectedContact(parsedContact);
		}
		if (parsedCarrierId !== selectedCarrierId) {
			setSelectedCarrierId(parsedCarrierId);
		}
		if (parsedOwner?.id !== selectedOwner?.id) {
			setSelectedOwner(parsedOwner);
		}
		if (JSON.stringify(parsedLineOfBusinessIds.sort()) !== JSON.stringify(selectedPolicyLineOfBusinessIds.sort())) {
			setSelectedPolicyLineOfBusinessIds(parsedLineOfBusinessIds);
		}
		if (parsedMinPremium !== minimumPremium) {
			setMinimumPremium(parsedMinPremium);
		}
		if (parsedMaxPremium !== maximumPremium) {
			setMaximumPremium(parsedMaxPremium);
		}
		if (parsedMinRenewalDate !== selectedMinimumRenewalDate) {
			setSelectedMinimumRenewalDate(parsedMinRenewalDate);
		}
		if (parsedMaxRenewalDate !== selectedMaximumRenewalDate) {
			setSelectedMaximumRenewalDate(parsedMaxRenewalDate);
		}
		if (parsedMinCommissions !== minimumCommissions) {
			setMinimumCommissions(parsedMinCommissions);
		}
		if (parsedMaxCommissions !== maximumCommissions) {
			setMaximumCommissions(parsedMaxCommissions);
		}
		if (parsedServicingRole1User?.id !== selectedServicingRole1User?.id) {
			setSelectedServicingRole1User(parsedServicingRole1User);
		}
		if (parsedServicingRole2User?.id !== selectedServicingRole2User?.id) {
			setSelectedServicingRole2User(parsedServicingRole2User);
		}

		// Reset the flag after state updates
		setTimeout(() => {
			isParsingFromUrlRef.current = false;
		}, 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterCriteria]);

	const hasActiveFilters = React.useMemo(() => {
		return (
			!!policyNumberSearch ||
			!!selectedContact ||
			!!selectedCarrierId ||
			!!selectedOwner ||
			selectedPolicyLineOfBusinessIds.length > 0 ||
			!!minimumPremium ||
			!!maximumPremium ||
			!!selectedMinimumRenewalDate ||
			!!selectedMaximumRenewalDate ||
			!!minimumCommissions ||
			!!maximumCommissions ||
			!!selectedServicingRole1User ||
			!!selectedServicingRole2User
		);
	}, [
		policyNumberSearch,
		selectedContact,
		selectedCarrierId,
		selectedOwner,
		selectedPolicyLineOfBusinessIds,
		minimumPremium,
		maximumPremium,
		selectedMinimumRenewalDate,
		selectedMaximumRenewalDate,
		minimumCommissions,
		maximumCommissions,
		selectedServicingRole1User,
		selectedServicingRole2User,
	]);

	const clearFilters = React.useCallback(() => {
		// Set parsing flag to prevent the first useEffect from interfering
		isParsingFromUrlRef.current = true;
		// Reset the ref to ensure the parsing effect sees a change when filterCriteria updates
		previousCriteriaStringRef.current = '';
		// Increment clear counter to force search remount
		searchClearCounterRef.current += 1;
		// Notify parent immediately that filters are cleared
		onFilterChange([]);
		// Reset all local state
		setPolicyNumberSearch('');
		setSelectedContact(null);
		setSelectedCarrierId(null);
		setSelectedOwner(null);
		setSelectedPolicyLineOfBusinessIds([]);
		setMinimumPremium('');
		setMaximumPremium('');
		setSelectedMinimumRenewalDate(null);
		setSelectedMaximumRenewalDate(null);
		setMinimumCommissions('');
		setMaximumCommissions('');
		setSelectedServicingRole1User(null);
		setSelectedServicingRole2User(null);
		// Reset the flag after state updates complete
		setTimeout(() => {
			isParsingFromUrlRef.current = false;
		}, 0);
	}, [onFilterChange]);

	const onCarrierSearchClear = React.useCallback(() => {
		setSelectedCarrierId(null);
	}, []);

	const selectCarrier = React.useCallback((carrier: Api.IInsurancePolicyCarrier) => {
		setSelectedCarrierId(carrier.id);
	}, []);

	const onOwnerCleared = React.useCallback(() => {
		setSelectedOwner(null);
	}, []);

	const onOwnerSelected = React.useCallback(({ selection }: { selection: Api.IEntity }) => {
		setSelectedOwner(selection as Api.IContact);
	}, []);

	const onServicingRole1UserCleared = React.useCallback(() => {
		setSelectedServicingRole1User(null);
	}, []);

	const onServicingRole1UserSelected = React.useCallback(({ selection }: { selection: Api.IEntity }) => {
		setSelectedServicingRole1User(selection as Api.IContact);
	}, []);

	const onServicingRole2UserCleared = React.useCallback(() => {
		setSelectedServicingRole2User(null);
	}, []);

	const onServicingRole2UserSelected = React.useCallback(({ selection }: { selection: Api.IEntity }) => {
		setSelectedServicingRole2User(selection as Api.IContact);
	}, []);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.filtersRow)}>
				{/* Carrier Filter */}
				<div className={css(styleSheet.filterGroup)}>
					<PolicyCarrierSearch
						placeholder='Carrier'
						className={css(dbStyleSheet.wideInput, dbStyleSheet.searchFieldReset)}
						onSelectCarrier={selectCarrier}
						onCreateCarrier={selectCarrier}
						onClear={onCarrierSearchClear}
						initialSearchFragment=''
					/>
				</div>

				{/* Policy Owner Filter */}
				<div className={css(styleSheet.filterGroup)}>
					<SimpleAutoCompleteSearchField
						key={`policy-owner-search-${searchClearCounterRef.current}`}
						onClear={onOwnerCleared}
						// @ts-ignore
						onItemSelected={onOwnerSelected}
						resultsLimit={5}
						pageSize={5}
						placeholder='Policy Owner'
						type={Api.ResourceAutoCompleteViewModelType.User}
						initialSearchQuery={selectedOwner?.id || ''}
						anchorStyles={[
							simpleAutoCompleteStyleSheet.searchFieldWhiteBackground,
							dbStyleSheet.comboBoxRightAutocompleteResponsive,
							dbStyleSheet.wideInputResponsive,
						]}
					/>
				</div>

				{/* Line of Business Filter */}
				<div className={css(styleSheet.filterGroup, dbStyleSheet.mediumInput)}>
					<PolicyLineOfBusinessFilterDropdown
						selectedPolicyLineOfBusinessIds={selectedPolicyLineOfBusinessIds}
						setSelectedPolicyLineOfBusinessIds={setSelectedPolicyLineOfBusinessIds}
						anchorClassName={css(dbStyleSheet.dropdownReset)}
					/>
				</div>

				{/* Renewal Date Filter */}
				<div className={css(styleSheet.filterGroup, dbStyleSheet.mediumInput)}>
					<FutureRenewalDateFilterDropdown
						selectedMinimumDate={selectedMinimumRenewalDate}
						setSelectedMinimumDate={setSelectedMinimumRenewalDate}
						selectedMaximumDate={selectedMaximumRenewalDate}
						setSelectedMaximumDate={setSelectedMaximumRenewalDate}
						anchorClassName={css(dbStyleSheet.dropdownReset)}
					/>
				</div>

				{/* Premium Filter */}
				<div className={css(styleSheet.filterGroup, dbStyleSheet.mediumInput)}>
					<MoneyFilterDropdown
						key={`premiumFilterDropdown-${minimumPremium}-${maximumPremium}`}
						initialMinimum={minimumPremium}
						initialMaximum={maximumPremium}
						onChange={value => {
							setMinimumPremium(value.minimum);
							setMaximumPremium(value.maximum);
						}}
						fieldName='Premium'
						anchorClassName={css(dbStyleSheet.dropdownReset)}
					/>
				</div>

				{/* Commissions Filter (conditional) */}
				{canEdit && (
					<div className={css(styleSheet.filterGroup, dbStyleSheet.mediumInput)}>
						<MoneyFilterDropdown
							key={`commissionsFilterDropdown-${minimumCommissions}-${maximumCommissions}`}
							initialMinimum={minimumCommissions}
							initialMaximum={maximumCommissions}
							onChange={value => {
								setMinimumCommissions(value.minimum);
								setMaximumCommissions(value.maximum);
							}}
							fieldName='Commissions'
							anchorClassName={css(dbStyleSheet.dropdownReset)}
						/>
					</div>
				)}

				{/* Servicing Role 1 User Filter (conditional) */}
				{canEdit && (
					<div className={css(styleSheet.filterGroup)}>
						<SimpleAutoCompleteSearchField
							key={`servicing-role-1-search-${searchClearCounterRef.current}`}
							onClear={onServicingRole1UserCleared}
							// @ts-ignore
							onItemSelected={onServicingRole1UserSelected}
							resultsLimit={5}
							pageSize={5}
							placeholder='Policy CSR'
							type={Api.ResourceAutoCompleteViewModelType.User}
							initialSearchQuery={selectedServicingRole1User?.id || ''}
							anchorStyles={[
								simpleAutoCompleteStyleSheet.searchFieldWhiteBackground,
								dbStyleSheet.comboBoxRightAutocompleteResponsive,
								dbStyleSheet.wideInputResponsive,
							]}
						/>
					</div>
				)}

				{/* Servicing Role 2 User Filter (conditional) */}
				{canEdit && (
					<div className={css(styleSheet.filterGroup)}>
						<SimpleAutoCompleteSearchField
							key={`servicing-role-2-search-${searchClearCounterRef.current}`}
							onClear={onServicingRole2UserCleared}
							// @ts-ignore
							onItemSelected={onServicingRole2UserSelected}
							resultsLimit={5}
							pageSize={5}
							placeholder='Policy Producer'
							type={Api.ResourceAutoCompleteViewModelType.User}
							initialSearchQuery={selectedServicingRole2User?.id || ''}
							anchorStyles={[
								simpleAutoCompleteStyleSheet.searchFieldWhiteBackground,
								dbStyleSheet.comboBoxRightAutocompleteResponsive,
								dbStyleSheet.wideInputResponsive,
							]}
						/>
					</div>
				)}

				{/* Clear Filters Button */}
				{hasActiveFilters && (
					<div className={css(styleSheet.clearFiltersContainer)}>
						<button
							className={css(baseStyleSheet.ctaButtonReverse, dbStyleSheet.clearSearchButton)}
							onClick={clearFilters}
						>
							<span>Clear Filters</span>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
